import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link as RouterLink } from "react-router-dom";
import api from "@/lib/api";
import { formatMoney, currencySymbol, formatWeight } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { WholesalerMaintenance } from "@/components/WholesalerMaintenance";
import {
  ArrowLeft, Globe, RefreshCw, KeyRound, Eye, Check, Link2, Coins, Gem,
  Upload as UploadIcon, Calculator, ShieldAlert, CheckCircle2, FileText,
  Image as ImageIcon, X, Plus, Trash2, Pencil, RotateCcw, Copy, AlertTriangle,
  FileSpreadsheet, Loader2, History,
} from "lucide-react";

const fmt = (v, digits = 2) =>
  v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: digits });

// Local re-export so we don't shadow the shared helper name elsewhere.
const money = formatMoney;

// CS 21 Turn 2 — wholesaler detail with 4 tabs.
export default function WholesalerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const canEdit = me?.role === "master_admin";
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = searchParams.get("tab") || "link";
  const setTab = (t) => setSearchParams({ tab: t }, { replace: true });

  const [w, setW] = useState(null);
  const reload = useCallback(() => api.get(`/wholesalers/${id}`).then((r) => setW(r.data)), [id]);
  useEffect(() => { reload(); }, [reload]);

  if (!w) return <div className="luxe-card p-10 text-center text-slate-400" data-testid="wholesaler-detail-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid={`wholesaler-detail-${id}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <button
            onClick={() => navigate("/wholesalers")}
            className="text-xs text-slate-500 hover:text-[#0D1B2A] flex items-center gap-1"
            data-testid="back-to-wholesalers"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Wholesalers
          </button>
          <h1 className="font-serif text-3xl md:text-4xl text-[#0D1B2A] mt-1">{w.name}</h1>
          <div className="text-sm text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            <span>{w.country || "—"}</span>
            <span>·</span>
            <span className="font-mono">{w.cost_currency}</span>
            {w.link_ready ? (
              <span className="ml-2 flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                <CheckCircle2 className="w-3 h-3" /> Link ready
              </span>
            ) : (
              <span
                className="ml-2 flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full"
                title={`Missing: ${(w.link_block_reasons || []).join(", ")}`}
              >
                <ShieldAlert className="w-3 h-3" /> Cost card incomplete
              </span>
            )}
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(t) => {
        // CS 21.7 — Dirty-state guard for the Diamond Rate-Card editor: warn
        // before leaving the tab with unsaved edits so an accidental click
        // doesn't wipe a half-filled card.
        if (window.__ashth_ratecard_dirty && tab === "diamond" && t !== "diamond") {
          if (!window.confirm("You have unsaved rate-card edits. Discard them?")) return;
          window.__ashth_ratecard_dirty = false;
        }
        setTab(t);
      }} className="space-y-4">
        <TabsList className="bg-[#0D1B2A]/5">
          <TabsTrigger value="link" data-testid="tab-link"><Link2 className="w-4 h-4 mr-1" /> Link &amp; PIN</TabsTrigger>
          <TabsTrigger value="cost" data-testid="tab-cost"><Coins className="w-4 h-4 mr-1" /> Cost Rate-Card</TabsTrigger>
          <TabsTrigger value="diamond" data-testid="tab-diamond"><Gem className="w-4 h-4 mr-1" /> Diamond Rate-Card</TabsTrigger>
          <TabsTrigger value="upload" data-testid="tab-upload"><UploadIcon className="w-4 h-4 mr-1" /> Upload &amp; Stock</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos"><ImageIcon className="w-4 h-4 mr-1" /> Photos</TabsTrigger>
          {/* CS 21.70 -- HO Trash tab (soft-deleted stock recovery) */}
          <TabsTrigger value="trash" data-testid="tab-trash"><Trash2 className="w-4 h-4 mr-1" /> Trash</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance"><History className="w-4 h-4 mr-1" /> Repair &amp; Price Log</TabsTrigger>
        </TabsList>

        <TabsContent value="link">
          <LinkAndPin w={w} canEdit={canEdit} reload={reload} />
        </TabsContent>
        <TabsContent value="cost">
          <CostRateCard wid={id} canEdit={canEdit} onSaved={reload} currency={w.cost_currency} />
        </TabsContent>
        <TabsContent value="diamond">
          <DiamondRateCard wid={id} canEdit={canEdit} currency={w.cost_currency} />
        </TabsContent>
        <TabsContent value="upload">
          <UploadAndStock wid={id} canEdit={canEdit} w={w} />
        </TabsContent>
        <TabsContent value="photos">
          <PhotosEmbeddedXlsxUpload wid={id} canEdit={canEdit} />
          <div className="mt-8">
            <PhotosBulkUpload canEdit={canEdit} />
          </div>
        </TabsContent>
        <TabsContent value="trash">
          <TrashTab wid={id} canEdit={canEdit} currency={w.cost_currency} />
        </TabsContent>
        <TabsContent value="maintenance">
          <WholesalerMaintenance wid={id} canEdit={canEdit} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Sub: Link & PIN ─────────────────────────────────────────────────
function LinkAndPin({ w, canEdit, reload }) {
  const [pinOpen, setPinOpen] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [deletePinOpen, setDeletePinOpen] = useState(false);
  const [newDeletePin, setNewDeletePin] = useState("");
  const [copied, setCopied] = useState(false);
  const url = useMemo(() => `${window.location.origin}/w/${w.access_token}`, [w.access_token]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select the link manually");
    }
  };

  const regen = async () => {
    if (!window.confirm("Regenerate link? The old URL will stop working.")) return;
    try {
      await api.post(`/wholesalers/${w.id}/token/regenerate`);
      toast.success("Link regenerated");
      reload();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.error === "cost_card_incomplete") {
        toast.error(`Link blocked — fill the cost rate-card first (missing: ${(detail.missing || []).join(", ")})`);
      } else {
        toast.error(formatApiError(detail) || "Regenerate failed");
      }
    }
  };

  const savePin = async () => {
    if (!/^\d{4,8}$/.test(newPin)) return toast.error("PIN must be 4-8 digits");
    try {
      await api.post(`/wholesalers/${w.id}/pin`, { pin: newPin });
      toast.success("PIN updated");
      setPinOpen(false);
      setNewPin("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  // CS 21.70 -- Delete PIN save (strictly 4 digits, HO-owned).
  const saveDeletePin = async () => {
    if (!/^\d{4}$/.test(newDeletePin)) return toast.error("Delete PIN must be exactly 4 digits");
    try {
      await api.post(`/wholesalers/${w.id}/delete-pin`, { pin: newDeletePin });
      toast.success("Delete PIN set — wholesaler portal can now soft-delete stock");
      setDeletePinOpen(false);
      setNewDeletePin("");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const unsetDeletePin = async () => {
    if (!window.confirm("Remove the delete PIN? Wholesaler portal delete becomes unavailable."))
      return;
    try {
      await api.delete(`/wholesalers/${w.id}/delete-pin`);
      toast.success("Delete PIN removed");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const resetLockout = async () => {
    try {
      await api.post(`/wholesalers/${w.id}/delete-pin/reset-lockout`);
      toast.success("Lockout cleared");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  // CS 21.70 -- Delete-PIN live status pill (fail counter surface).
  const dpFails = Number(w.delete_pin_fails || 0);
  const dpLockedUntil = w.delete_pin_locked_until
    ? new Date(w.delete_pin_locked_until)
    : null;
  const dpLocked = !!(dpLockedUntil && dpLockedUntil > new Date());
  const dpBadgeColor = !w.delete_pin_set
    ? "text-slate-500 bg-slate-50 border-slate-200"
    : dpLocked
      ? "text-red-800 bg-red-50 border-red-200"
      : dpFails > 0
        ? "text-amber-800 bg-amber-50 border-amber-200"
        : "text-emerald-800 bg-emerald-50 border-emerald-200";

  return (
    <div className="luxe-card p-6 space-y-4" data-testid="wholesaler-link-card">
      <div className="flex items-center gap-2 text-sm">
        <Globe className="w-4 h-4 text-[#C9A84C]" />
        <span className="font-medium">Public access link</span>
      </div>
      {!w.link_ready && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2" data-testid="link-block-banner">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Cost rate-card incomplete</div>
            <p className="text-xs mt-1">
              Fill the HO cost rate-card before regenerating the wholesaler link. Missing:{" "}
              <span className="font-mono">{(w.link_block_reasons || []).join(", ") || "—"}</span>
            </p>
          </div>
        </div>
      )}
      <div className="rounded-md border border-[#0D1B2A]/10 bg-white p-4 space-y-3">
        <div className="text-xs font-mono break-all text-slate-700 select-all" data-testid="wholesaler-link-url">{url}</div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={copy}
            data-testid="wholesaler-link-copy"
            className={`px-3 py-1.5 rounded-md border text-xs flex items-center gap-1.5 transition-colors ${
              copied ? "border-emerald-500 text-emerald-600 bg-emerald-50" : "border-[#0D1B2A]/15 hover:bg-[#FAF8F3]"
            }`}
          >
            {copied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Link2 className="w-3.5 h-3.5" /> Copy</>}
          </button>
          <button
            onClick={() => window.open(url, "_blank")}
            data-testid="wholesaler-link-preview"
            className="px-3 py-1.5 rounded-md border border-[#0D1B2A]/15 text-xs flex items-center gap-1.5 hover:bg-[#FAF8F3]"
          >
            <Eye className="w-3.5 h-3.5" /> Preview
          </button>
          {canEdit && (
            <>
              <button
                onClick={regen}
                disabled={!w.link_ready}
                data-testid="wholesaler-link-regenerate"
                className="px-3 py-1.5 rounded-md border border-[#0D1B2A]/15 text-xs flex items-center gap-1.5 hover:bg-[#FAF8F3] disabled:opacity-40 disabled:cursor-not-allowed"
                title={w.link_ready ? "Rotate token" : "Fill cost rate-card to unlock"}
              >
                <RefreshCw className="w-3.5 h-3.5" /> Regenerate
              </button>
              <button
                onClick={() => { setPinOpen(true); setNewPin(""); }}
                data-testid="wholesaler-link-set-pin"
                className="px-3 py-1.5 rounded-md border border-[#0D1B2A]/15 text-xs flex items-center gap-1.5 hover:bg-[#FAF8F3]"
              >
                <KeyRound className="w-3.5 h-3.5" /> Set PIN
              </button>
              <button
                onClick={() => { setDeletePinOpen(true); setNewDeletePin(""); }}
                data-testid="wholesaler-link-set-delete-pin"
                className="px-3 py-1.5 rounded-md border border-red-200 text-red-700 text-xs flex items-center gap-1.5 hover:bg-red-50"
                title="4-digit PIN gating the per-piece Delete button on the wholesaler portal"
              >
                <Trash2 className="w-3.5 h-3.5" /> Set Delete PIN
              </button>
            </>
          )}
        </div>

        {/* CS 21.70 -- live delete-PIN status pill + fail counter */}
        <div className="flex items-center flex-wrap gap-2 pt-1"
             data-testid="delete-pin-status-row">
          <span
            className={`text-[11px] px-2 py-0.5 rounded-full border ${dpBadgeColor}`}
            data-testid="delete-pin-status-pill"
          >
            Delete PIN:{" "}
            {!w.delete_pin_set
              ? "Not set"
              : dpLocked
                ? `Locked until ${dpLockedUntil.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                : dpFails > 0
                  ? `Active · ${dpFails}/5 wrong tries`
                  : "Active"}
          </span>
          {canEdit && w.delete_pin_set && (
            <>
              {dpLocked && (
                <button
                  onClick={resetLockout}
                  data-testid="delete-pin-reset-lockout"
                  className="text-[11px] px-2 py-0.5 rounded-md border border-[#0D1B2A]/15 hover:bg-[#FAF8F3]"
                >
                  Clear lockout
                </button>
              )}
              <button
                onClick={unsetDeletePin}
                data-testid="delete-pin-unset"
                className="text-[11px] px-2 py-0.5 rounded-md border border-[#0D1B2A]/15 hover:bg-[#FAF8F3]"
              >
                Remove
              </button>
            </>
          )}
        </div>
      </div>

      <Dialog open={pinOpen} onOpenChange={(o) => { if (!o) { setPinOpen(false); setNewPin(""); } }}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Set access PIN</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">4-8 digits. Shared with the wholesaler alongside the public link.</p>
          <Input
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
            maxLength={8}
            inputMode="numeric"
            placeholder="0000"
            className="text-2xl tracking-[0.5em] text-center h-14"
            data-testid="wholesaler-pin-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPinOpen(false); setNewPin(""); }}>Cancel</Button>
            <Button onClick={savePin} className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="wholesaler-pin-save">Save PIN</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CS 21.70 -- Delete PIN modal */}
      <Dialog
        open={deletePinOpen}
        onOpenChange={(o) => { if (!o) { setDeletePinOpen(false); setNewDeletePin(""); } }}
      >
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {w.delete_pin_set ? "Change Delete PIN" : "Set Delete PIN"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Exactly 4 digits. Wholesalers must enter this PIN to soft-delete a
            piece from their portal. Deleted pieces stay recoverable from the
            HO Trash tab. 5 wrong tries → 10-minute lockout.
          </p>
          <Input
            value={newDeletePin}
            onChange={(e) => setNewDeletePin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            maxLength={4}
            inputMode="numeric"
            placeholder="0000"
            className="text-2xl tracking-[0.5em] text-center h-14"
            data-testid="wholesaler-delete-pin-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeletePinOpen(false); setNewDeletePin(""); }}>
              Cancel
            </Button>
            <Button
              onClick={saveDeletePin}
              disabled={!/^\d{4}$/.test(newDeletePin)}
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
              data-testid="wholesaler-delete-pin-save"
            >
              Save Delete PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Sub: HO Trash (CS 21.70) ────────────────────────────────────────
function TrashTab({ wid, canEdit }) {
  const [items, setItems] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState({});

  const reload = useCallback(async () => {
    try {
      const [tr, au] = await Promise.all([
        api.get(`/wholesalers/${wid}/trash`),
        api.get(`/wholesalers/${wid}/delete-audit`),
      ]);
      setItems(tr.data.items || []);
      setAudit(au.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load Trash");
    }
  }, [wid]);

  useEffect(() => { reload(); }, [reload]);

  const restore = async (jc) => {
    setBusy((b) => ({ ...b, [jc]: true }));
    try {
      const r = await api.post(`/wholesalers/${wid}/stock/${encodeURIComponent(jc)}/restore`);
      toast.success(`Restored to ${r.data.restored_to || "in_stock"}`);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Restore failed");
    } finally {
      setBusy((b) => ({ ...b, [jc]: false }));
    }
  };

  if (items === null) {
    return <div className="luxe-card p-6 text-slate-400" data-testid="trash-loading">Loading Trash…</div>;
  }

  return (
    <div className="space-y-6" data-testid="wholesaler-trash">
      <div className="luxe-card p-6 space-y-4">
        <div className="flex items-center gap-2 text-sm">
          <Trash2 className="w-4 h-4 text-red-600" />
          <span className="font-medium">Deleted stock (recoverable)</span>
          <span className="text-slate-400 text-xs" data-testid="trash-count">
            · {items.length} piece{items.length === 1 ? "" : "s"}
          </span>
        </div>

        {items.length === 0 ? (
          <div className="text-sm text-slate-400 italic" data-testid="trash-empty">
            No deleted pieces. Anything soft-deleted from the wholesaler portal
            will appear here for recovery.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((p) => (
              <div
                key={p.jewel_code}
                className="rounded-md border border-red-100 bg-red-50/40 p-3 space-y-2"
                data-testid={`trash-piece-${p.jewel_code}`}
              >
                <div className="flex items-start gap-3">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.jewel_code} className="w-16 h-16 object-cover rounded-md" />
                  ) : (
                    <div className="w-16 h-16 rounded-md bg-slate-100 flex items-center justify-center text-slate-300">
                      <ImageIcon className="w-6 h-6" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[11px] text-slate-500 truncate">{p.style_no || "—"}</div>
                    <div className="font-medium text-[#0D1B2A] truncate">{p.jewel_code}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {[p.category, p.karat ? `${p.karat}KT` : (p.metal || null),
                        p.net_wt_g > 0 ? `${formatWeight(p.net_wt_g)} g` : null]
                        .filter(Boolean).join(" · ")}
                    </div>
                    <div className="text-[10px] text-red-700 mt-1">
                      Deleted {p.deleted_at ? new Date(p.deleted_at).toLocaleString() : "—"}
                      {p.deleted_prev_status && p.deleted_prev_status !== "in_stock" ? (
                        <span className="ml-1 text-slate-500">
                          (was <span className="font-mono">{p.deleted_prev_status}</span>)
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
                {canEdit && (
                  <button
                    onClick={() => restore(p.jewel_code)}
                    disabled={!!busy[p.jewel_code]}
                    data-testid={`trash-restore-${p.jewel_code}`}
                    className="w-full text-xs px-2 py-1.5 rounded-md border border-[#0D1B2A]/15 bg-white hover:bg-[#FAF8F3] flex items-center justify-center gap-1.5 disabled:opacity-40"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Restore
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="luxe-card p-6 space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <FileText className="w-4 h-4 text-slate-500" />
          <span className="font-medium">Delete-audit log</span>
          <span className="text-slate-400 text-xs">· {audit.length} entr{audit.length === 1 ? "y" : "ies"}</span>
        </div>
        {audit.length === 0 ? (
          <div className="text-sm text-slate-400 italic">No delete or restore events yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="delete-audit-table">
              <thead className="text-left text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="py-1.5 pr-3">When</th>
                  <th className="py-1.5 pr-3">Action</th>
                  <th className="py-1.5 pr-3">Piece</th>
                  <th className="py-1.5 pr-3">Actor</th>
                  <th className="py-1.5 pr-3">Detail</th>
                </tr>
              </thead>
              <tbody>
                {audit.slice(0, 100).map((a, i) => (
                  <tr key={a.id || i} className="border-t border-slate-100 align-top"
                      data-testid={`audit-row-${i}`}>
                    <td className="py-1.5 pr-3 whitespace-nowrap font-mono text-slate-500">
                      {a.at ? new Date(a.at).toLocaleString([], { hour12: false }) : "—"}
                    </td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">
                      <span className={`px-1.5 py-0.5 rounded ${a.action === "delete" ? "bg-red-100 text-red-700"
                        : a.action === "restore" ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-100 text-slate-600"}`}>
                        {a.action}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3 font-mono">{a.jewel_code}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{a.actor || "—"}</td>
                    <td className="py-1.5 pr-3 text-slate-500">
                      {a.previous_status ? `prev: ${a.previous_status}` : ""}
                      {a.fails ? ` · fails: ${a.fails}` : ""}
                      {a.locked_until ? ` · locked` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub: Cost rate-card editor ──────────────────────────────────────
const PURITY_KEYS = ["9", "14", "18", "950"];
export function CostRateCard({ wid, canEdit, onSaved, currency }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const sym = currencySymbol(currency || "USD");

  useEffect(() => {
    api.get(`/wholesalers/${wid}/cost-settings`).then((r) => setDoc(r.data));
  }, [wid]);

  if (!doc) return <div className="luxe-card p-6 text-slate-400">Loading cost settings…</div>;

  const upd = (k, v) => setDoc({ ...doc, [k]: v });
  const updPurity = (k, v) => setDoc({
    ...doc,
    purity_factors: { ...(doc.purity_factors || {}), [k]: v === "" ? 0 : Number(v) },
  });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        cost_gold_rate_per_g: Number(doc.cost_gold_rate_per_g) || 0,
        labour_mode: doc.labour_mode || "per_gram",
        labour_cost_rate: Number(doc.labour_cost_rate) || 0,
        gold_loss_pct: Number(doc.gold_loss_pct) || 0,  // CS 21.6
        igi_mode: doc.igi_mode || "actual",
        igi_cost_per_piece: Number(doc.igi_cost_per_piece) || 0,
        igi_cost_per_carat: Number(doc.igi_cost_per_carat) || 0,
        // CS 21.35 -- landed_expense_pct MOVED to the wholesaler's own
        // My Selling Rates sheet. HO no longer writes it here.
        purity_factors: doc.purity_factors,
      };
      const { data } = await api.put(`/wholesalers/${wid}/cost-settings`, payload);
      setDoc(data);
      toast.success("Cost rate-card saved");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const recompute = async () => {
    if (!window.confirm("Recompute live cost for all computed-source pieces? Sold pieces are skipped to preserve audit history.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/wholesalers/${wid}/recompute-cost`);
      toast.success(`Recomputed ${data.recomputed} piece(s)${data.skipped_sold ? `, skipped ${data.skipped_sold} sold` : ""}.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Recompute failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="luxe-card p-6 space-y-6" data-testid="cost-ratecard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Coins className="w-4 h-4 text-[#C9A84C]" />
          <span className="font-medium">HO-owned cost rate-card</span>
        </div>
        {canEdit && (
          <button
            onClick={recompute}
            disabled={busy}
            data-testid="recompute-cost-btn"
            className="px-3 py-1.5 rounded-md border border-[#0D1B2A]/15 text-xs flex items-center gap-1.5 hover:bg-[#FAF8F3] disabled:opacity-50"
            title="Rebuild live cost on every computed piece from these settings"
          >
            <Calculator className="w-3.5 h-3.5" /> Recompute cost
          </button>
        )}
      </div>

      {/* Spreadsheet-style grid for cost levers */}
      <div className="overflow-x-auto rounded-md border border-[#0D1B2A]/10">
        <table className="w-full text-sm">
          <thead className="bg-[#0D1B2A]/5">
            <tr>
              <th className="text-left px-3 py-2 font-medium w-1/3">Cost lever</th>
              <th className="text-right px-3 py-2 font-medium">Value</th>
              <th className="text-left px-3 py-2 font-medium">Unit</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-[#0D1B2A]/5">
              <td className="px-3 py-2">Gold cost rate</td>
              <td className="px-3 py-1 text-right">
                <Input
                  type="number" step="0.0001"
                  value={doc.cost_gold_rate_per_g ?? 0}
                  onChange={(e) => upd("cost_gold_rate_per_g", e.target.value)}
                  disabled={!canEdit}
                  className="text-right font-mono"
                  data-testid="cost-gold-rate"
                />
              </td>
              <td className="px-3 py-2 text-xs text-slate-500">{sym}/g (pure)</td>
            </tr>
            <tr className="border-t border-[#0D1B2A]/5">
              <td className="px-3 py-2">Labour cost rate</td>
              <td className="px-3 py-1 text-right">
                <Input
                  type="number" step="0.01"
                  value={doc.labour_cost_rate ?? 0}
                  onChange={(e) => upd("labour_cost_rate", e.target.value)}
                  disabled={!canEdit}
                  className="text-right font-mono"
                  data-testid="cost-labour-rate"
                />
              </td>
              <td className="px-3 py-2 text-xs text-slate-500">{sym}/g (net)</td>
            </tr>
            <tr className="border-t border-[#0D1B2A]/5" data-testid="cost-gold-loss-row">
              <td className="px-3 py-2">
                Gold loss %
                <span className="ml-2 text-xs text-slate-400">folds into labour</span>
              </td>
              <td className="px-3 py-1 text-right">
                <Input
                  type="number" step="0.01" min="0" max="100"
                  value={doc.gold_loss_pct ?? 0}
                  onChange={(e) => upd("gold_loss_pct", e.target.value)}
                  disabled={!canEdit}
                  className="text-right font-mono"
                  data-testid="cost-gold-loss-pct"
                />
              </td>
              <td className="px-3 py-2 text-xs text-slate-500">% of gold value</td>
            </tr>
            <tr className="border-t border-[#0D1B2A]/5">
              <td className="px-3 py-2">IGI / cert mode</td>
              <td className="px-3 py-1 text-right">
                <Select value={doc.igi_mode || "actual"} onValueChange={(v) => upd("igi_mode", v)} disabled={!canEdit}>
                  <SelectTrigger className="w-full" data-testid="cost-igi-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="actual">actual (invoice passthrough)</SelectItem>
                    <SelectItem value="per_piece">per piece (fixed)</SelectItem>
                    <SelectItem value="per_carat">per carat ({sym}/ct × diamond carats)</SelectItem>
                    <SelectItem value="none">none</SelectItem>
                  </SelectContent>
                </Select>
              </td>
              <td className="px-3 py-2 text-xs text-slate-500">
                {doc.igi_mode === "per_piece" ? `fixed ${sym}/piece` :
                 doc.igi_mode === "per_carat" ? `${sym}/ct × dia_wt` :
                 doc.igi_mode === "none" ? "no cert cost" :
                 "reads invoice IGI"}
              </td>
            </tr>
            {doc.igi_mode === "per_piece" && (
              <tr className="border-t border-[#0D1B2A]/5">
                <td className="px-3 py-2 pl-6">IGI fixed cost</td>
                <td className="px-3 py-1 text-right">
                  <Input
                    type="number" step="0.01"
                    value={doc.igi_cost_per_piece ?? 0}
                    onChange={(e) => upd("igi_cost_per_piece", e.target.value)}
                    disabled={!canEdit}
                    className="text-right font-mono"
                    data-testid="cost-igi-fixed"
                  />
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">{sym}/piece</td>
              </tr>
            )}
            {doc.igi_mode === "per_carat" && (
              <tr className="border-t border-[#0D1B2A]/5">
                <td className="px-3 py-2 pl-6">IGI rate per carat</td>
                <td className="px-3 py-1 text-right">
                  <Input
                    type="number" step="0.01"
                    value={doc.igi_cost_per_carat ?? 0}
                    onChange={(e) => upd("igi_cost_per_carat", e.target.value)}
                    disabled={!canEdit}
                    className="text-right font-mono"
                    data-testid="cost-igi-per-carat"
                  />
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">{sym}/ct × diamond carats</td>
              </tr>
            )}
            {/* CS 21.35 -- "Landed expense %" REMOVED from HO cost rate-card.
                The wholesaler now owns this on their My Selling Rates sheet
                (they know their own shipping/duty). HO controls only true
                cost inputs (gold rate, labour rate, purity, cert). */}
          </tbody>
        </table>
      </div>

      {/* Purity factor table */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Purity factors (pure-gold conversion)</div>
        <div className="overflow-x-auto rounded-md border border-[#0D1B2A]/10">
          <table className="w-full text-sm">
            <thead className="bg-[#0D1B2A]/5">
              <tr>
                {PURITY_KEYS.map((k) => (
                  <th key={k} className="px-3 py-2 font-medium text-center">{k === "950" ? "PT 950" : `${k}KT`}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {PURITY_KEYS.map((k) => (
                  <td key={k} className="px-3 py-1 text-center">
                    <Input
                      type="number" step="0.001"
                      value={(doc.purity_factors || {})[k] ?? 0}
                      onChange={(e) => updPurity(k, e.target.value)}
                      disabled={!canEdit}
                      className="text-center font-mono"
                      data-testid={`cost-purity-${k}`}
                    />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {canEdit && (
        <div className="flex justify-end">
          <Button
            onClick={save}
            disabled={busy}
            className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white"
            data-testid="cost-save"
          >
            {busy ? "Saving…" : "Save cost rate-card"}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Sub: Diamond rate-card v2 editor (CS 21.7) ─────────────────────
// Legacy shape-group constant retained ONLY for the assumed-shape
// reconciliation dropdown further down the file (rows appear when the Excel
// importer couldn't infer a piece's shape from its style_no).
const SHAPE_GROUPS = [
  "Rounds", "Asth/Heart/Marquise",
  "Oval/Pear/Radiant/Princess/Cushion", "Emerald",
];

// Per-shape spreadsheet: each shape column has its own bracket ladder
// (rows). HO can add/rename/remove shape columns AND add/delete bracket
// rows per shape. Dirty-state guard warns on tab close / tab switch.
// ─── CS 21.17 -- Alias input with local raw-text buffer ─────────────────
// The alias field used to re-derive its value from the parsed array on
// every keystroke -- `value = list.join(", ")` + `onChange = split(",")` --
// which meant typing a comma triggered `[...].join(", ")` and the trailing
// empty was filtered, dropping the character. Now we keep the raw text in
// local state while typing, and only commit the parsed list on blur.
function AliasInput({ shape, value, onCommit, disabled, testId }) {
  const [text, setText] = useState(() => (Array.isArray(value) ? value.join(", ") : ""));
  const [focused, setFocused] = useState(false);
  // Sync from props ONLY when the field is not focused (avoid clobbering
  // the user's in-progress edit if a parent state update re-renders).
  useEffect(() => {
    if (!focused) setText(Array.isArray(value) ? value.join(", ") : "");
  }, [value, focused]);
  const commit = () => {
    setFocused(false);
    const list = text.split(",").map((s) => s.trim()).filter(Boolean);
    onCommit?.(list);
    // Re-sync display text so a trailing comma or extra whitespace is cleaned.
    setText(list.join(", "));
  };
  return (
    <Input
      value={text}
      onChange={(e) => setText(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={commit}
      disabled={disabled}
      placeholder="e.g. oval, pear, marquise"
      className="mt-1 h-8 text-xs font-mono"
      data-testid={testId}
    />
  );
}

// CS 21.67 -- Helpers for the diamond rate-card. Bracket rows need
// stable React keys during the edit session (user can add / delete /
// reorder rows in place), so we stamp a transient `_uid` on load and
// strip it back off before persisting.
let _uidCounter = 0;
const _newUid = () => `bkt-${Date.now()}-${++_uidCounter}`;

function _stampBracketUids(doc) {
  if (!doc?.standard?.brackets_by_shape) return doc;
  const bbs = doc.standard.brackets_by_shape;
  const stamped = {};
  for (const shape of Object.keys(bbs)) {
    stamped[shape] = (bbs[shape] || []).map((row) =>
      row && row._uid ? row : { ...row, _uid: _newUid() }
    );
  }
  return {
    ...doc,
    standard: { ...doc.standard, brackets_by_shape: stamped },
  };
}

function _stripBracketUids(doc) {
  if (!doc?.standard?.brackets_by_shape) return doc;
  const bbs = doc.standard.brackets_by_shape;
  const stripped = {};
  for (const shape of Object.keys(bbs)) {
    stripped[shape] = (bbs[shape] || []).map((row) => {
      const { _uid, ...rest } = row || {};
      return rest;
    });
  }
  return {
    ...doc,
    standard: { ...doc.standard, brackets_by_shape: stripped },
  };
}



export function DiamondRateCard({ wid, canEdit, currency }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  // CS 21.38 -- editable multipliers for the "rebuild sell from cost" panel.
  const [b2bMult, setB2bMult] = useState("2");
  const [custMult, setCustMult] = useState("6");
  const [pending, setPending] = useState({});      // CS 21.8 section 5 -- {raw_shape: piece_count}
  const [autoFixes, setAutoFixes] = useState([]);   // CS 21.8 section 1 -- last save's auto-nudges
  const [warnings, setWarnings] = useState([]);     // CS 21.8 section 1 -- last save's gap warnings
  const sym = currencySymbol(currency);

  const refreshPending = () => {
    api.get(`/wholesalers/${wid}/pending-shapes`)
      .then((r) => setPending(r.data?.pending_shapes || {}))
      .catch(() => setPending({}));
  };

  useEffect(() => {
    api.get(`/wholesalers/${wid}/diamond-ratecard`).then((r) => {
      // CS 21.67 -- stamp a stable per-bracket-row `_uid` when the API
      // payload lands, so `deleteBracket`/`addBracket` can reorder the
      // rows without React losing focus on the wrong Input. Stripped
      // back out before the save PUT so nothing leaks to the DB.
      setDoc(_stampBracketUids(r.data));
      setDirty(false);
      window.__ashth_ratecard_dirty = false;
    });
    refreshPending();
  }, [wid]);

  // Wire tab-close guard: browser beforeunload fires on refresh / tab close.
  useEffect(() => {
    const handler = (e) => {
      if (!window.__ashth_ratecard_dirty) return;
      e.preventDefault();
      e.returnValue = "";  // required for the native "leave site?" prompt
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const markDirty = () => {
    setDirty(true);
    window.__ashth_ratecard_dirty = true;
  };

  if (!doc) {
    return <div className="luxe-card p-6 text-slate-400">Loading diamond rate-card…</div>;
  }

  const shapes = doc.standard?.shapes || [];
  const bbs = doc.standard?.brackets_by_shape || {};

  const setShapeBrackets = (shape, rows) => {
    setDoc((d) => ({
      ...d,
      standard: {
        ...d.standard,
        brackets_by_shape: { ...d.standard.brackets_by_shape, [shape]: rows },
      },
    }));
    markDirty();
  };

  const updateCell = (shape, idx, side, value) => {
    const rows = [...(bbs[shape] || [])];
    rows[idx] = { ...rows[idx], [side]: value === "" ? null : Number(value) };
    setShapeBrackets(shape, rows);
  };

  const updateRange = (shape, idx, key, value) => {
    const rows = [...(bbs[shape] || [])];
    rows[idx] = { ...rows[idx], [key]: key === "range" ? value : Number(value) };
    setShapeBrackets(shape, rows);
  };

  const addBracket = (shape) => {
    const rows = [...(bbs[shape] || [])];
    rows.push({ _uid: _newUid(), range: "0.00-0.00", min: 0, max: 0, cost: null, sell: null, sell_b2b: null, sell_customer: null });
    setShapeBrackets(shape, rows);
  };

  const deleteBracket = (shape, idx) => {
    if (!window.confirm(`Delete this bracket row from ${shape}?`)) return;
    const rows = [...(bbs[shape] || [])];
    rows.splice(idx, 1);
    setShapeBrackets(shape, rows);
  };

  const addShape = () => {
    const name = (window.prompt("New shape column name:") || "").trim();
    if (!name) return;
    if (shapes.includes(name)) { toast.error(`Shape "${name}" already exists`); return; }
    setDoc((d) => ({
      ...d,
      standard: {
        ...d.standard,
        shapes: [...d.standard.shapes, name],
        brackets_by_shape: { ...d.standard.brackets_by_shape, [name]: [] },
      },
    }));
    markDirty();
  };

  const renameShape = (oldName) => {
    const newName = (window.prompt(`Rename "${oldName}" to:`, oldName) || "").trim();
    if (!newName || newName === oldName) return;
    if (shapes.includes(newName)) { toast.error(`Shape "${newName}" already exists`); return; }
    setDoc((d) => {
      const nextBbs = { ...d.standard.brackets_by_shape };
      nextBbs[newName] = nextBbs[oldName];
      delete nextBbs[oldName];
      return {
        ...d,
        standard: {
          ...d.standard,
          shapes: d.standard.shapes.map((s) => (s === oldName ? newName : s)),
          brackets_by_shape: nextBbs,
        },
      };
    });
    markDirty();
  };

  const deleteShape = (name) => {
    if (!window.confirm(`Delete the ENTIRE "${name}" column and all its bracket rows?`)) return;
    setDoc((d) => {
      const nextBbs = { ...d.standard.brackets_by_shape };
      delete nextBbs[name];
      return {
        ...d,
        standard: {
          ...d.standard,
          shapes: d.standard.shapes.filter((s) => s !== name),
          brackets_by_shape: nextBbs,
        },
      };
    });
    markDirty();
  };

  // CS 21.35 -- per-shape "Markup % on cost" MOVED to the wholesaler's own
  // My Selling Rates sheet (see SellingRatesSheet in WholesalerPortal.jsx).
  // The wholesaler decides their own margins per tier; HO does not seed them.
  // The old HO-side setShapeMarkup / applyShapeMarkup helpers were removed.

  const save = async () => {
    setBusy(true);
    try {
      // CS 21.67 -- strip transient `_uid`s (used only for stable React
      // keys on this screen) before persisting so the DB doc stays
      // clean and API validators don't see unknown fields.
      const { data } = await api.put(
        `/wholesalers/${wid}/diamond-ratecard`,
        _stripBracketUids(doc),
      );
      setDoc(_stampBracketUids(data));
      setDirty(false);
      window.__ashth_ratecard_dirty = false;
      // CS 21.8 -- surface auto-fixes + warnings from the validator response.
      setAutoFixes(data?.validation?.auto_fixes || []);
      setWarnings(data?.validation?.warnings || []);
      refreshPending();
      const fixCount = (data?.validation?.auto_fixes || []).length;
      toast.success(`Rate-card saved${fixCount ? ` (${fixCount} auto-nudge${fixCount > 1 ? "s" : ""})` : ""}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // CS 21.8 -- overlap errors come back as {validation: {errors: [...]}}.
      if (detail?.validation?.errors?.length) {
        const errs = detail.validation.errors;
        toast.error(`Cannot save: ${errs.length} bracket error${errs.length > 1 ? "s" : ""}. See panel above.`);
        setAutoFixes([]);
        setWarnings(errs);  // reuse warnings strip for error display
      } else {
        toast.error(formatApiError(detail) || "Save failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const duplicateShape = async (source) => {
    const target = (window.prompt(`Duplicate "${source}" as:`, `${source} (copy)`) || "").trim();
    if (!target) return;
    if ((doc.standard?.shapes || []).includes(target)) {
      toast.error(`Shape "${target}" already exists`);
      return;
    }
    if (dirty && !window.confirm("Save current edits before duplicating? Cancel duplicate?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/wholesalers/${wid}/diamond-ratecard/duplicate-shape`, { source, target });
      setDoc(data);
      setDirty(false);
      window.__ashth_ratecard_dirty = false;
      toast.success(`Cloned "${source}" as "${target}" (aliases empty -- attach as needed)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Duplicate failed");
    } finally {
      setBusy(false);
    }
  };

  const updateAliases = (shape, next) => {
    // CS 21.17 -- accepts EITHER a comma-separated string (legacy caller) or
    // a pre-parsed array (new AliasInput onCommit). Normalises to trimmed,
    // deduped, non-empty entries. No lowercasing here -- the routing engine
    // does its own case-insensitive match, so we preserve the exact string
    // the HO typed for display.
    let aliasList;
    if (Array.isArray(next)) {
      aliasList = next;
    } else {
      aliasList = String(next || "").split(",");
    }
    aliasList = aliasList.map((s) => String(s || "").trim()).filter(Boolean);
    // Case-insensitive dedupe -- keep first occurrence.
    const seen = new Set();
    aliasList = aliasList.filter((s) => {
      const k = s.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    setDoc((d) => ({
      ...d,
      standard: {
        ...d.standard,
        aliases: { ...(d.standard.aliases || {}), [shape]: aliasList },
      },
    }));
    markDirty();
  };

  const attachAliasQuick = (rawShape, targetShape) => {
    const existing = (doc.standard?.aliases?.[targetShape] || []).map((a) => a.toLowerCase());
    if (existing.includes(rawShape.toLowerCase())) {
      toast.info(`"${rawShape}" already aliases "${targetShape}"`);
      return;
    }
    const nextAliases = { ...(doc.standard?.aliases || {}) };
    nextAliases[targetShape] = [...(nextAliases[targetShape] || []), rawShape];
    setDoc((d) => ({ ...d, standard: { ...d.standard, aliases: nextAliases } }));
    markDirty();
    toast.success(`Attached "${rawShape}" as alias to "${targetShape}" -- Save to persist.`);
  };

  // CS 21.51 -- confirmation dialog + Undo toast on Reset.
  // The confirm wording spells out the destructive effect explicitly per
  // spec ("all costs change; all sells return to 0"). Undo restores the
  // pre-reset snapshot exactly and remains available until the next save.
  const reset = async () => {
    const confirmed = window.confirm(
      `Reset the ${currency || "USD"} diamond rate-card to defaults?\n\n` +
      `This overwrites ALL Cost and Sell values on this wholesaler's card:\n` +
      `  • All Cost cells switch to the ${currency || "USD"} default costs\n` +
      `  • All Sell B2B / Sell Customer cells return to 0\n\n` +
      `Pieces will show $0 sell until the wholesaler re-applies the ×N ` +
      `multiplier. You can Undo this from the toast until the next save.`
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/wholesalers/${wid}/diamond-ratecard/reset`);
      setDoc(data);
      setDirty(false);
      window.__ashth_ratecard_dirty = false;
      const canUndo = !!data?.undo_available;
      const opts = canUndo
        ? {
            action: {
              label: "Undo",
              onClick: async () => {
                try {
                  await api.post(`/wholesalers/${wid}/diamond-ratecard/undo-reset`);
                  // Re-fetch the restored card.
                  const { data: restored } = await api.get(
                    `/wholesalers/${wid}/diamond-ratecard`
                  );
                  setDoc(restored);
                  setDirty(false);
                  window.__ashth_ratecard_dirty = false;
                  toast.success("Previous rate-card restored");
                } catch (err) {
                  toast.error(
                    formatApiError(err.response?.data?.detail) ||
                      "Undo unavailable (rate-card already saved)"
                  );
                }
              },
            },
            duration: 15000,
          }
        : { duration: 6000 };
      toast.success(
        canUndo
          ? "Rate-card reset to defaults — Undo available until next save"
          : "Rate-card reset to defaults",
        opts
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="luxe-card p-6 space-y-6" data-testid="diamond-ratecard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Gem className="w-4 h-4 text-[#C9A84C]" />
          <span className="font-medium">Diamond rate-card · {currency || "USD"} per carat</span>
          {dirty && (
            <span data-testid="ratecard-dirty-badge"
                  className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full">
              Unsaved
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500">
          Per-shape brackets · HO writes <span className="font-mono text-[#0D1B2A]">cost</span> &amp; both sell tiers.
          Cost cells are shared; sell splits into B2B and Customer.
        </div>
      </div>

      {/* CS 21.38 -- Dynamic-multiplier re-seed. Type any number, click Apply.
          Overwrites existing sell values on this tier (confirms first) so the
          "×N" intent actually rebuilds sell from cost. Skips cost=0 brackets
          and reports the skipped count. */}
      {canEdit && (
        <div className="p-3 rounded-md bg-[#0D1B2A]/3 border border-[#0D1B2A]/8 space-y-2" data-testid="reseed-panel">
          <div className="text-xs text-slate-600">Rebuild sell from cost — type a multiplier, click Apply:</div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 min-w-[42px]">B2B</span>
              <Input
                type="number" step="0.01" min="0"
                value={b2bMult}
                onChange={(e) => setB2bMult(e.target.value)}
                className="font-mono text-right h-8 w-20"
                data-testid="reseed-b2b-multiplier-input"
                placeholder="2"
              />
              <span className="text-xs text-slate-400">×</span>
              <Button
                variant="outline" size="sm"
                onClick={async () => {
                  const mult = Number(b2bMult);
                  if (!Number.isFinite(mult) || mult <= 0) { toast.error("Enter a positive B2B multiplier"); return; }
                  if (dirty && !window.confirm("You have unsaved edits. Save first? Re-seed will proceed on the last saved state.")) return;
                  const bbs = doc?.standard?.brackets_by_shape || {};
                  const existingCount = Object.values(bbs).flat().filter((r) => (Number(r?.sell_b2b) || 0) > 0).length;
                  if (existingCount > 0 && !window.confirm(
                    `This will overwrite ${existingCount} sell rate${existingCount === 1 ? "" : "s"} in B2B with cost × ${mult}. Continue?`
                  )) return;
                  try {
                    const { data } = await api.post(`/wholesalers/${wid}/diamond-ratecard/reseed-sell`,
                      { tier: "b2b", multiplier: mult, overwrite_manual: true });
                    const parts = [`B2B re-seeded ${data.changed} cell${data.changed === 1 ? "" : "s"} at cost × ${mult}`];
                    if (data.skipped_zero_cost > 0) parts.push(`${data.skipped_zero_cost} skipped (no cost)`);
                    toast.success(parts.join(" · "));
                    const rc = await api.get(`/wholesalers/${wid}/diamond-ratecard`);
                    setDoc(rc.data); setDirty(false); window.__ashth_ratecard_dirty = false;
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Re-seed failed"); }
                }}
                data-testid="reseed-b2b-apply"
              >Apply to B2B</Button>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 min-w-[70px]">Customer</span>
              <Input
                type="number" step="0.01" min="0"
                value={custMult}
                onChange={(e) => setCustMult(e.target.value)}
                className="font-mono text-right h-8 w-20"
                data-testid="reseed-customer-multiplier-input"
                placeholder="6"
              />
              <span className="text-xs text-slate-400">×</span>
              <Button
                variant="outline" size="sm"
                onClick={async () => {
                  const mult = Number(custMult);
                  if (!Number.isFinite(mult) || mult <= 0) { toast.error("Enter a positive Customer multiplier"); return; }
                  if (dirty && !window.confirm("You have unsaved edits. Save first? Re-seed will proceed on the last saved state.")) return;
                  const bbs = doc?.standard?.brackets_by_shape || {};
                  const existingCount = Object.values(bbs).flat().filter((r) => (Number(r?.sell_customer) || 0) > 0).length;
                  if (existingCount > 0 && !window.confirm(
                    `This will overwrite ${existingCount} sell rate${existingCount === 1 ? "" : "s"} in Customer with cost × ${mult}. Continue?`
                  )) return;
                  try {
                    const { data } = await api.post(`/wholesalers/${wid}/diamond-ratecard/reseed-sell`,
                      { tier: "customer", multiplier: mult, overwrite_manual: true });
                    const parts = [`Customer re-seeded ${data.changed} cell${data.changed === 1 ? "" : "s"} at cost × ${mult}`];
                    if (data.skipped_zero_cost > 0) parts.push(`${data.skipped_zero_cost} skipped (no cost)`);
                    toast.success(parts.join(" · "));
                    const rc = await api.get(`/wholesalers/${wid}/diamond-ratecard`);
                    setDoc(rc.data); setDirty(false); window.__ashth_ratecard_dirty = false;
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Re-seed failed"); }
                }}
                data-testid="reseed-customer-apply"
              >Apply to Customer</Button>
            </div>
          </div>
          <div className="text-[11px] text-slate-500">
            Overwrites every non-zero-cost bracket&apos;s sell in the chosen tier. Cost cells (HO-owned) are never touched. Cost = 0 brackets are skipped.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {shapes.map((shape) => {
          const rows = bbs[shape] || [];
          return (
            <div key={shape} className="rounded-md border border-[#0D1B2A]/10 overflow-hidden"
                 data-testid={`ratecard-shape-${shape}`}>
              <div className="flex items-center justify-between px-3 py-2 bg-[#0D1B2A]/5">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-[#0D1B2A]">{shape}</span>
                  <span className="text-[10px] text-slate-500">({rows.length} brackets)</span>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => duplicateShape(shape)}
                      className="p-1 text-slate-500 hover:text-[#C9A84C]"
                      title="Duplicate shape (clone brackets + rates, empty aliases)"
                      data-testid={`shape-duplicate-${shape}`}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => renameShape(shape)}
                      className="p-1 text-slate-500 hover:text-[#0D1B2A]"
                      title="Rename shape"
                      data-testid={`shape-rename-${shape}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => deleteShape(shape)}
                      className="p-1 text-slate-500 hover:text-red-600"
                      title="Delete shape"
                      data-testid={`shape-delete-${shape}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
              {/* CS 21.8 section 3 -- editable alias list per shape.
                  CS 21.17 -- uses a local raw-text buffer so typing a comma
                  is not eaten by the split/join round-trip. Commits parsed
                  list on blur; commits again on Save via aliasBuffer flush. */}
              <div className="px-3 py-2 bg-white border-b border-[#0D1B2A]/5">
                <label className="text-[10px] uppercase tracking-wider text-slate-500">
                  Aliases (comma-separated -- raw shape strings from uploaded stock that route here)
                </label>
                <AliasInput
                  shape={shape}
                  value={doc.standard?.aliases?.[shape] || []}
                  onCommit={(list) => updateAliases(shape, list)}
                  disabled={!canEdit}
                  testId={`shape-aliases-${shape}`}
                />
              </div>
              {/* CS 21.35 -- per-shape "Markup % on cost" UI REMOVED from HO
                  side. It now lives in the wholesaler's own My Selling Rates
                  sheet, per-tier (see SellingRatesSheet in WholesalerPortal.jsx). */}
              <table className="w-full text-xs">
                <thead className="bg-white">
                  <tr className="text-slate-500">
                    <th className="px-2 py-1 text-left font-medium">Range (ct)</th>
                    <th className="px-2 py-1 text-right font-medium">Min</th>
                    <th className="px-2 py-1 text-right font-medium">Max</th>
                    <th className="px-2 py-1 text-right font-medium font-mono text-[10px]">cost ({sym})</th>
                    <th className="px-2 py-1 text-right font-medium font-mono text-[10px]">sell B2B ({sym})</th>
                    <th className="px-2 py-1 text-right font-medium font-mono text-[10px]">sell Cust ({sym})</th>
                    <th className="w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row._uid || `${shape}-${idx}`} className="border-t border-[#0D1B2A]/5">
                      <td className="px-1 py-1">
                        <Input
                          value={row.range ?? ""}
                          onChange={(e) => updateRange(shape, idx, "range", e.target.value)}
                          disabled={!canEdit}
                          className="font-mono h-7 px-2 text-xs"
                          data-testid={`bracket-range-${shape}-${idx}`}
                        />
                      </td>
                      <td className="px-1 py-1">
                        <Input
                          type="number" step="0.001"
                          value={row.min ?? ""}
                          onChange={(e) => updateRange(shape, idx, "min", e.target.value)}
                          disabled={!canEdit}
                          className="font-mono h-7 px-2 text-xs text-right"
                        />
                      </td>
                      <td className="px-1 py-1">
                        <Input
                          type="number" step="0.001"
                          value={row.max ?? ""}
                          onChange={(e) => updateRange(shape, idx, "max", e.target.value)}
                          disabled={!canEdit}
                          className="font-mono h-7 px-2 text-xs text-right"
                        />
                      </td>
                      <td className="px-1 py-1">
                        <Input
                          type="number"
                          value={row.cost ?? ""}
                          onChange={(e) => updateCell(shape, idx, "cost", e.target.value)}
                          disabled={!canEdit}
                          className="font-mono h-7 px-2 text-xs text-right"
                          data-testid={`bracket-cost-${shape}-${idx}`}
                        />
                      </td>
                      <td className="px-1 py-1">
                        <Input
                          type="number"
                          value={row.sell_b2b ?? row.sell ?? ""}
                          onChange={(e) => updateCell(shape, idx, "sell_b2b", e.target.value)}
                          disabled={!canEdit}
                          className="font-mono h-7 px-2 text-xs text-right bg-slate-50"
                          data-testid={`bracket-sell-b2b-${shape}-${idx}`}
                        />
                      </td>
                      <td className="px-1 py-1">
                        <Input
                          type="number"
                          value={row.sell_customer ?? row.sell ?? ""}
                          onChange={(e) => updateCell(shape, idx, "sell_customer", e.target.value)}
                          disabled={!canEdit}
                          className="font-mono h-7 px-2 text-xs text-right bg-[#C9A84C]/5"
                          data-testid={`bracket-sell-customer-${shape}-${idx}`}
                        />
                      </td>
                      <td className="px-1 py-1 text-center">
                        {canEdit && (
                          <button
                            onClick={() => deleteBracket(shape, idx)}
                            className="p-1 text-slate-400 hover:text-red-600"
                            title="Delete bracket"
                            data-testid={`bracket-delete-${shape}-${idx}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-3 text-center text-slate-400 text-xs">
                        No brackets yet — click &quot;+ Add bracket&quot; below.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {canEdit && (
                <div className="px-3 py-2 bg-white border-t border-[#0D1B2A]/5">
                  <button
                    onClick={() => addBracket(shape)}
                    className="text-xs text-[#C9A84C] hover:text-[#C9A84C]/70 flex items-center gap-1"
                    data-testid={`bracket-add-${shape}`}
                  >
                    <Plus className="w-3 h-3" /> Add bracket
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {canEdit && (
        <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-[#0D1B2A]/10">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={addShape}
              className="border-dashed"
              data-testid="shape-add"
            >
              <Plus className="w-4 h-4 mr-1" /> Add shape column
            </Button>
            <Button
              variant="outline"
              onClick={reset}
              disabled={busy}
              className="border-slate-300 text-slate-600"
              data-testid="ratecard-reset"
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Reset to {currency || "USD"} defaults
            </Button>
          </div>
          <Button
            onClick={save}
            disabled={busy || !dirty}
            className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white"
            data-testid="diamond-save"
          >
            {busy ? "Saving…" : dirty ? "Save diamond rate-card" : "Saved"}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Sub: Upload & Stock ────────────────────────────────────────────
function UploadAndStock({ wid, canEdit, w }) {
  const [file, setFile] = useState(null);
  const [costSource, setCostSource] = useState("computed");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stock, setStock] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [pendingShapes, setPendingShapes] = useState({}); // jewel_code -> {group, table}

  const loadStock = useCallback(async () => {
    // For HO admin view of stock we use the public token + a sentinel: there is
    // no admin endpoint to read stock; pull via public/token using a known PIN
    // is brittle. Instead, surface a server-side admin route via shipments+stock.
    // We use the public list endpoint with the wholesaler's token — admins can
    // see it because PIN auth is by hash; we skip stock list in the admin UI
    // and just surface stats + shape-confirmation candidates from the import
    // result. This keeps the admin layer scoped to import + cost.
    try {
      const { data } = await api.get(`/wholesalers/${wid}`);
      setShipments([]); // future: dedicated admin stock route
      void data; // currently unused
    } catch (err) {
      console.warn("[wholesaler-detail] loadStock probe failed.", err);
    }
    void setStock;
  }, [wid]);
  useEffect(() => { loadStock(); }, [loadStock]);

  const upload = async () => {
    if (!file) return toast.error("Choose an Excel file");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("cost_source", costSource);
      const { data } = await api.post(`/wholesalers/${wid}/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported · ${data.inserted} new · ${data.updated} updated${data.skipped_sold?.length ? ` · ${data.skipped_sold.length} sold kept` : ""}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const confirmShapes = async () => {
    const payload = Object.entries(pendingShapes).map(([jewel_code, v]) => ({
      jewel_code, diamond_shape_group: v.group, diamond_table: v.table,
    }));
    if (!payload.length) return toast.error("No shapes to confirm");
    try {
      const { data } = await api.post(`/wholesalers/${wid}/shapes/confirm`, payload);
      toast.success(`Updated ${data.updated} piece(s)`);
      setPendingShapes({});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Confirm failed");
    }
  };

  const assumedRows = (result?.assumed_shapes || []);

  return (
    <div className="space-y-6" data-testid="upload-stock">
      <div className="luxe-card p-6 space-y-4">
        <div className="flex items-center gap-2 text-sm">
          <UploadIcon className="w-4 h-4 text-[#C9A84C]" />
          <span className="font-medium">Import wholesaler stock (Ashth Export Detail or Ruchi format, .xlsx/.xls)</span>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Excel file</label>
            <Input
              type="file"
              accept=".xlsx,.xlsm,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={!canEdit || busy}
              data-testid="upload-file-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Cost source</label>
            <Select value={costSource} onValueChange={setCostSource} disabled={!canEdit || busy}>
              <SelectTrigger data-testid="upload-cost-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="computed">computed (HO-dynamic)</SelectItem>
                <SelectItem value="imported">imported (invoice passthrough)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {canEdit && (
          <div className="flex justify-end">
            <Button
              onClick={upload}
              disabled={!file || busy}
              className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white"
              data-testid="upload-submit"
            >
              {busy ? "Uploading…" : "Upload & parse"}
            </Button>
          </div>
        )}
        <p className="text-[11px] text-slate-400">
          Cost source = <span className="font-mono">computed</span> → live cost rebuilds from the HO cost rate-card +
          diamond .cost cells. Cost source = <span className="font-mono">imported</span> → live cost mirrors the
          invoice columns verbatim. invoice_cost_* fields are stored either way for audit.
        </p>
      </div>

      {result && (
        <div className="luxe-card p-6 space-y-3" data-testid="upload-result">
          <div className="flex items-center gap-2 text-sm font-medium">
            <FileText className="w-4 h-4 text-emerald-600" /> Last upload
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Stat label="Inserted" value={result.inserted} />
            <Stat label="Updated" value={result.updated} />
            <Stat label="Errors" value={result.errors?.length || 0} tone={result.errors?.length ? "amber" : "slate"} />
            <Stat label="Cost source" value={result.cost_source} mono />
          </div>
          {result.skipped_sold?.length > 0 && (
            <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800" data-testid="upload-skipped-sold">
              {result.skipped_sold.length} sold piece(s) left unchanged (sold status, sale data and locked prices kept):{" "}
              <span className="font-mono">{result.skipped_sold.join(", ")}</span>
            </div>
          )}
          {result.warnings?.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-1">
              {result.warnings.map((wn, i) => <div key={`wn-${i}`}>⚠ {wn}</div>)}
            </div>
          )}
          {result.errors?.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-amber-700">View {result.errors.length} cross-check mismatch(es)</summary>
              <ul className="mt-2 max-h-48 overflow-y-auto space-y-1 font-mono">
                {result.errors.map((er, i) => (
                  <li key={`er-${i}`} className="text-slate-600">
                    {er.jewel_code}: Δ {fmt(er.delta)} (sum {fmt(er.sum)} vs total {fmt(er.total)})
                  </li>
                ))}
              </ul>
            </details>
          )}
          {result.ledger?.booked_gold_rate_per_g && (
            <div className="text-xs text-slate-500">
              Booked gold rate: <span className="font-mono text-[#0D1B2A]">{fmt(result.ledger.booked_gold_rate_per_g, 4)}</span> / g
            </div>
          )}
        </div>
      )}

      {assumedRows.length > 0 && (
        <div className="luxe-card p-6 space-y-3" data-testid="shape-confirm">
          <div className="text-sm font-medium">Confirm assumed shapes ({assumedRows.length})</div>
          <p className="text-xs text-slate-500">
            These Style-Nos couldn&rsquo;t be auto-classified. Pick the diamond shape group + table to lock pricing in.
          </p>
          <div className="overflow-x-auto rounded-md border border-[#0D1B2A]/10">
            <table className="w-full text-sm">
              <thead className="bg-[#0D1B2A]/5">
                <tr>
                  <th className="px-3 py-2 text-left">Jewel</th>
                  <th className="px-3 py-2 text-left">Style</th>
                  <th className="px-3 py-2 text-left">Shape group</th>
                  <th className="px-3 py-2 text-left">Table</th>
                </tr>
              </thead>
              <tbody>
                {assumedRows.map((row) => (
                  <tr key={row.jewel_code} className="border-t border-[#0D1B2A]/5">
                    <td className="px-3 py-1 font-mono text-xs">{row.jewel_code}</td>
                    <td className="px-3 py-1 font-mono text-xs">{row.style_no}</td>
                    <td className="px-3 py-1">
                      <Select
                        value={pendingShapes[row.jewel_code]?.group || ""}
                        onValueChange={(v) => setPendingShapes({
                          ...pendingShapes,
                          [row.jewel_code]: { ...(pendingShapes[row.jewel_code] || { table: "standard" }), group: v },
                        })}
                      >
                        <SelectTrigger className="h-8 w-full" data-testid={`shape-group-${row.jewel_code}`}><SelectValue placeholder="Pick…" /></SelectTrigger>
                        <SelectContent>
                          {SHAPE_GROUPS.map((sg) => <SelectItem key={sg} value={sg}>{sg}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-1">
                      <Select
                        value={pendingShapes[row.jewel_code]?.table || "standard"}
                        onValueChange={(v) => setPendingShapes({
                          ...pendingShapes,
                          [row.jewel_code]: { ...(pendingShapes[row.jewel_code] || { group: SHAPE_GROUPS[0] }), table: v },
                        })}
                      >
                        <SelectTrigger className="h-8 w-full" data-testid={`shape-table-${row.jewel_code}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["standard", "rose_cut", "fancy_cut", "fancy_colour", "briolette", "single_cut"].map((t) =>
                            <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end">
            <Button onClick={confirmShapes} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="shape-confirm-submit">
              Confirm shapes
            </Button>
          </div>
        </div>
      )}

      <RouterLink to={`/wholesalers`} className="text-xs text-slate-500 hover:text-[#0D1B2A] inline-flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to wholesalers
      </RouterLink>
    </div>
  );
}

function Stat({ label, value, mono = false, tone = "slate" }) {
  const colour = tone === "amber" ? "text-amber-700" : "text-[#0D1B2A]";
  return (
    <div className="rounded-md border border-[#0D1B2A]/10 bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg ${mono ? "font-mono" : "font-medium"} ${colour}`}>{value}</div>
    </div>
  );
}

// ─── CS 21.14 -- Embedded-image .xlsx ingest (primary HO photo book path) ─
// Column A = embedded image (anchored to its row), col B = JewelCode,
// col C = StyleCode. Backend parses ws._images by anchor.row, dedupes by
// SHA-256, resizes to 800px long edge JPEG q82, mirrors image_url onto
// this wholesaler's stock rows only (nothing writes to the shared products
// catalog unless a product row already existed for that jewel_code).
function PhotosEmbeddedXlsxUpload({ wid, canEdit }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) return toast.error("Pick a .xlsx workbook");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/wholesalers/${wid}/photos/xlsx-embedded`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000, // 10 min for large photo books (30-50MB)
      });
      setResult(data);
      toast.success(
        `Mapped ${data.mapped_count} · ${data.rows_without_image_count} without image · ${data.unmatched_count} unmatched`,
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="luxe-card p-6 space-y-4" data-testid="photos-xlsx-embedded">
      <div className="flex items-center gap-2 text-sm">
        <FileSpreadsheet className="w-4 h-4 text-[#C9A84C]" />
        <span className="font-medium">Photo book (embedded-image .xlsx)</span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
          Primary
        </span>
      </div>
      <p className="text-xs text-slate-500">
        Upload a workbook where column&nbsp;A is the embedded image, column&nbsp;B is <span className="font-mono">JewelCode</span>,
        column&nbsp;C is <span className="font-mono">StyleCode</span>. One piece per row. Images are matched by row anchor,
        JewelCode first then StyleCode. Duplicates are de-duped by hash; anything unmatched is listed below.
      </p>
      <div className="flex flex-wrap gap-3 items-center">
        <Input
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
          disabled={!canEdit || busy}
          data-testid="photos-xlsx-input"
          className="flex-1 min-w-[220px]"
        />
        {canEdit && (
          <Button
            onClick={upload}
            disabled={!file || busy}
            className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white"
            data-testid="photos-xlsx-upload-btn"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing…</> : "Upload & map"}
          </Button>
        )}
      </div>
      {busy && (
        <p className="text-[11px] text-slate-400">
          Large photo books (30&ndash;50&nbsp;MB) can take a minute or two — the server is decoding, resizing, and
          de-duping every image. Please stay on this page.
        </p>
      )}
      {result && (
        <div className="space-y-4 pt-2 border-t border-slate-100" data-testid="photos-xlsx-result">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Mapped" value={result.mapped_count} />
            <Stat label="Without image" value={result.rows_without_image_count} tone={result.rows_without_image_count ? "amber" : "slate"} />
            <Stat label="Unmatched" value={result.unmatched_count} tone={result.unmatched_count ? "amber" : "slate"} />
            <Stat label="Unique blobs" value={result.dedupe_unique_blobs} mono />
          </div>
          <div className="text-[11px] text-slate-500 -mt-2">
            Match breakdown: <span className="font-mono">{result.matched_by_jewel_code_count}</span> by JewelCode ·{" "}
            <span className="font-mono">{result.matched_by_style_code_count}</span> by StyleCode fallback ·{" "}
            <span className="font-mono">{result.images_seen}</span> images seen in sheet
          </div>
          {result.rows_without_image_count > 0 && (
            <details className="text-xs" data-testid="photos-xlsx-without-image">
              <summary className="cursor-pointer text-amber-700">
                {result.rows_without_image_count} row{result.rows_without_image_count === 1 ? "" : "s"} without an image
              </summary>
              <ul className="mt-2 max-h-56 overflow-y-auto space-y-1 font-mono border border-amber-200 rounded-md bg-amber-50/40 p-3">
                {result.rows_without_image.map((r, i) => (
                  <li key={`ni-${i}`} className="flex items-center gap-3 text-slate-700">
                    <span className="text-slate-400 w-14 shrink-0">row {r.row}</span>
                    <span className="w-32 shrink-0">{r.jewel_code || "—"}</span>
                    <span className="text-slate-500">{r.style_code || "—"}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {result.unmatched_count > 0 && (
            <details className="text-xs" data-testid="photos-xlsx-unmatched">
              <summary className="cursor-pointer text-amber-700">
                {result.unmatched_count} image{result.unmatched_count === 1 ? "" : "s"} unmatched (no piece in this
                wholesaler&rsquo;s stock)
              </summary>
              <ul className="mt-2 max-h-56 overflow-y-auto space-y-1 font-mono border border-amber-200 rounded-md bg-amber-50/40 p-3">
                {result.unmatched.map((u, i) => (
                  <li key={`u-${i}`} className="flex items-center gap-3 text-slate-700">
                    <span className="text-slate-400 w-14 shrink-0">row {u.row}</span>
                    <span className="w-32 shrink-0">{u.jewel_code || "—"}</span>
                    <span className="text-slate-500">{u.style_code || "—"}</span>
                    {u.error && <span className="text-red-500">{u.error}</span>}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-slate-400 mt-2">
                Unmatched images are not persisted — fix the JewelCode/StyleCode in the sheet (or the stock upload) and re-run.
              </p>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub: HO bulk photo upload (auto-map by filename) ────────────────
function PhotosBulkUpload({ canEdit }) {
  const [files, setFiles] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState({}); // filename -> jewel_code

  const upload = async () => {
    if (!files.length) return toast.error("Pick one or more image files");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/wholesalers/photos/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Linked ${data.matched_count} · ${data.unmatched_count} unmatched`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const assignOne = async (filename, jewel_code) => {
    const photo = (result?.unmatched || []).find((u) => u.filename === filename);
    if (!photo || !jewel_code) return;
    // We don't have a temp URL for unmatched files (we never uploaded them).
    // For the v1 flow: ask the HO to re-upload that file with the matched
    // jewel_code in the filename. We surface the manual mapping but the
    // photo bytes need re-upload. (Future: store unmatched files in a tray.)
    toast.message(`Rename ${filename} → ${jewel_code}.${(filename.split(".").pop() || "jpg")} and re-upload.`);
  };

  return (
    <div className="space-y-6" data-testid="photos-bulk">
      <div className="luxe-card p-6 space-y-4">
        <div className="flex items-center gap-2 text-sm">
          <ImageIcon className="w-4 h-4 text-[#C9A84C]" />
          <span className="font-medium">Bulk photo upload</span>
        </div>
        <p className="text-xs text-slate-500">
          Drop image files whose filename matches a piece&rsquo;s jewel code (e.g. <span className="font-mono">JWL-001.jpg</span>)
          or style number. Photos write to the shared catalog photo store and surface across the wholesaler portal +
          franchise/distributor pages — single source of truth, no duplication.
        </p>
        <Input
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
          disabled={!canEdit || busy}
          data-testid="photos-file-input"
        />
        {files.length > 0 && (
          <div className="text-xs text-slate-500">
            {files.length} file{files.length === 1 ? "" : "s"} selected
            <button
              onClick={() => setFiles([])}
              className="ml-2 text-[#0D1B2A] underline-offset-2 hover:underline"
            >
              clear
            </button>
          </div>
        )}
        {canEdit && (
          <div className="flex justify-end">
            <Button
              onClick={upload}
              disabled={!files.length || busy}
              className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white"
              data-testid="photos-upload-btn"
            >
              {busy ? "Uploading…" : `Upload ${files.length || ""} & auto-map`}
            </Button>
          </div>
        )}
      </div>

      {result && (
        <div className="luxe-card p-6 space-y-4" data-testid="photos-result">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Matched & linked" value={result.matched_count} />
            <Stat label="Unmatched" value={result.unmatched_count} tone={result.unmatched_count ? "amber" : "slate"} />
          </div>
          {result.matched_count > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-emerald-700">
                {result.matched_count} matched piece{result.matched_count === 1 ? "" : "s"}
              </summary>
              <ul className="mt-2 max-h-48 overflow-y-auto space-y-1 font-mono">
                {result.matched.map((m, i) => (
                  <li key={`m-${i}`} className="text-slate-600 flex items-center justify-between gap-2">
                    <span>{m.filename}</span>
                    <span className="text-[#0D1B2A]">→ {m.jewel_code}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {result.unmatched_count > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-amber-700 mb-2">
                Unmatched files — pick a jewel code or rename + re-upload
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50/50 divide-y divide-amber-200">
                {result.unmatched.map((u) => (
                  <div key={u.filename} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`unmatched-${u.base}`}>
                    <span className="font-mono text-xs text-slate-700 flex-1 min-w-[120px]">{u.filename}</span>
                    <Input
                      placeholder="jewel_code"
                      value={manual[u.filename] || ""}
                      onChange={(e) => setManual({ ...manual, [u.filename]: e.target.value })}
                      className="w-48 h-9 font-mono text-xs"
                      data-testid={`unmatched-input-${u.base}`}
                    />
                    <button
                      onClick={() => assignOne(u.filename, manual[u.filename])}
                      className="text-xs px-3 py-2 rounded-md border border-amber-300 bg-white hover:bg-amber-100"
                      data-testid={`unmatched-assign-${u.base}`}
                    >
                      Map
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-2">
                Files that fail auto-matching aren&rsquo;t persisted to storage. Rename the file to match the jewel code (or style number) and re-upload.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

